import React, { useState, useEffect, useRef, useMemo } from "react";
import { PlayerState } from "../../../core/protocol/types";
import PlayerBoardView, { calculateGameViewHeight } from "./PlayerBoardView";
import { TEAM_COLORS } from "../../../core/constants";
import Palette from "../../../core/engine/models/palette";
import { IGame } from "../../../core/engine/types/game";

interface OtherPlayersViewProps {
  players: Map<number, PlayerState>;
  localConnectionId?: string;
  palette?: Palette;
  containerWidth: number;
  containerHeight: number;
  getRemoteGame?: (numPlayer: number) => IGame | undefined;
}

const OtherPlayersView: React.FC<OtherPlayersViewProps> = ({
  players,
  localConnectionId,
  palette,
  containerWidth,
  containerHeight,
  getRemoteGame,
}) => {
  const flexContainerRef = useRef<HTMLDivElement>(null);
  const [boardSize, setBoardSize] = useState(100);

  const allOtherPlayers = Array.from(players.values()).filter(
    (player) =>
      player.connectionId !== localConnectionId && !player.isDedicatedServer
  );

  // Calculate layout parameters (size and count) - Memoized based on constraints
  const { numPlayersToShow, calculatedBoardSize } = useMemo(() => {
    if (allOtherPlayers.length === 0) {
      return { numPlayersToShow: 0, calculatedBoardSize: 100 };
    }

    if (containerWidth <= 0 || containerHeight <= 0) {
      return { numPlayersToShow: allOtherPlayers.length, calculatedBoardSize: 100 };
    }

    // Fixed values (matching CSS)
    const padding = 4;
    const gap = 12;
    const playerInfoHeight = 20;
    const playerInfoMargin = 10;

    const minBoardWidth = 50;

    // Available space (subtract padding on all sides)
    const availableWidth = containerWidth - padding * 2;
    const availableHeight = containerHeight - padding * 2;

    // Calculate max players that fit with minimum board size (single row)
    const maxPlayers = Math.floor(
      (availableWidth + gap) / (minBoardWidth + gap)
    );
    const numPlayers = Math.min(allOtherPlayers.length, maxPlayers);

    if (numPlayers === 0) {
      return { numPlayersToShow: 0, calculatedBoardSize: minBoardWidth };
    }

    // HEIGHT CONSTRAINT FIRST - this is the limiting factor
    // totalItemHeight = boardHeight + playerInfoHeight + playerInfoMargin <= availableHeight
    const totalInfoHeight = playerInfoHeight + playerInfoMargin;
    const maxHeightForBoard = availableHeight - totalInfoHeight;
    if (maxHeightForBoard <= 0) {
      // Not enough height even for player info
      return {
        numPlayersToShow: 0,
        calculatedBoardSize: minBoardWidth,
      };
    }

    // Calculate max width from height constraint
    // We need to find the board width where calculateGameViewHeight(width) <= maxHeightForBoard
    // Since height increases with width, we can use binary search or approximate
    // For now, we'll use a simple iterative approach to find the max width
    let maxWidthFromHeight = minBoardWidth;
    for (let testWidth = minBoardWidth; testWidth <= availableWidth; testWidth += 5) {
      const testHeight = calculateGameViewHeight(testWidth);
      if (testHeight <= maxHeightForBoard) {
        maxWidthFromHeight = testWidth;
      } else {
        break;
      }
    }

    // Adjust minBoardWidth if it doesn't fit in available height
    const effectiveMinBoardWidth = Math.min(minBoardWidth, maxWidthFromHeight);

    // Now calculate width constraint based on number of players
    const widthForBoard =
      (availableWidth - gap * (numPlayers - 1)) / numPlayers;

    // Board size is limited by BOTH width and height - take the smaller one
    // HEIGHT is usually the limiting factor, so we prioritize it
    let finalSize = Math.min(widthForBoard, maxWidthFromHeight);
    finalSize = Math.max(effectiveMinBoardWidth, Math.floor(finalSize));

    // Double-check: verify it fits vertically
    const boardHeight = calculateGameViewHeight(finalSize);
    const totalItemHeight = boardHeight + totalInfoHeight;

    if (totalItemHeight > availableHeight) {
      // Force it to fit height exactly - find the width that gives us the exact height
      const exactMaxHeightForBoard = availableHeight - totalInfoHeight;
      // Binary search for the exact width
      let exactWidthFromHeight = minBoardWidth;
      for (let testWidth = minBoardWidth; testWidth <= availableWidth; testWidth += 1) {
        const testHeight = calculateGameViewHeight(testWidth);
        if (testHeight <= exactMaxHeightForBoard) {
          exactWidthFromHeight = testWidth;
        } else {
          break;
        }
      }
      finalSize = Math.max(
        effectiveMinBoardWidth,
        Math.floor(exactWidthFromHeight)
      );

      // If it still doesn't fit width-wise, reduce number of players
      const totalWidth = finalSize * numPlayers + gap * (numPlayers - 1);
      if (totalWidth > availableWidth) {
        // Recalculate with fewer players
        const newNumPlayers = Math.max(
          1,
          Math.floor((availableWidth + gap) / (finalSize + gap))
        );
        if (newNumPlayers < numPlayers) {
          return {
            numPlayersToShow: newNumPlayers,
            calculatedBoardSize: finalSize,
          };
        }
      }
    }

    // Verify width still fits
    const totalWidth = finalSize * numPlayers + gap * (numPlayers - 1);
    let verifiedNumPlayers = numPlayers;
    if (totalWidth > availableWidth) {
      // Reduce number of players if needed
      verifiedNumPlayers = Math.max(
        1,
        Math.floor((availableWidth + gap) / (finalSize + gap))
      );
      if (verifiedNumPlayers < numPlayers) {
        // Recalculate board size for fewer players
        const newWidthForBoard =
          (availableWidth - gap * (verifiedNumPlayers - 1)) /
          verifiedNumPlayers;
        finalSize = Math.min(newWidthForBoard, maxWidthFromHeight);
        finalSize = Math.max(minBoardWidth, Math.floor(finalSize));
      }
    }

    return {
      numPlayersToShow: verifiedNumPlayers,
      calculatedBoardSize: finalSize,
    };
  }, [allOtherPlayers.length, containerWidth, containerHeight]);

  // Derive displayed players from the fresh allOtherPlayers array
  const displayedPlayers = allOtherPlayers.slice(0, numPlayersToShow);

  useEffect(() => {
    setBoardSize(calculatedBoardSize);
  }, [calculatedBoardSize]);

  return (
    <div className="other-players-column">
      <div className="other-players-boards">
        {displayedPlayers.length === 0 ? (
          <div className="no-players">No other players</div>
        ) : (
          <div className="boards-flex" ref={flexContainerRef}>
            {displayedPlayers.map((player) => {
              const remoteGame = getRemoteGame?.(player.numPlayer);

              return (
                <div
                  key={player.connectionId}
                  className="player-board-item"
                  style={{ width: `${boardSize}px` }}
                >
                  <div className="player-board-wrapper">
                    <PlayerBoardView
                      game={remoteGame}
                      playerName={player.name}
                      width={boardSize}
                    />
                  </div>
                  <div
                    className="player-info"
                    style={{
                      width: `${boardSize}px`,
                      maxWidth: `${boardSize}px`,
                    }}
                  >
                    <h4
                      style={{
                        color: player.isGone
                          ? "rgba(128, 128, 128, 0.6)"
                          : TEAM_COLORS[player.team ?? 0],
                        margin: 0,
                        padding: 0,
                        fontSize: "12px",
                        fontWeight: "bold",
                        opacity: player.isGone ? 0.6 : 1,
                      }}
                      title={player.name}
                    >
                      {player.name}
                      {player.isGone ? " *" : ""}
                    </h4>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <style>{`
        .other-players-column {
          display: flex;
          flex-direction: column;
          min-width: 0;
          min-height: 0;
          overflow: hidden;
          flex: 1;
          height: 100%;
          max-height: 100%;
        }
        
        .other-players-boards {
          background: transparent;
          backdrop-filter: none;
          border: none;
          padding: 0;
          border-radius: 0;
          color: var(--text-primary);
          flex: 1;
          min-width: 0;
          min-height: 0;
          max-height: 100%;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: none;
          box-sizing: border-box;
        }
        
        
        .boards-flex {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 12px;
          justify-content: center;
          align-items: flex-start;
          flex: 1;
          min-height: 0;
          max-height: 100%;
          overflow-y: hidden;
          overflow-x: auto;
          padding: 4px;
          box-sizing: border-box;
          width: 100%;
          -webkit-overflow-scrolling: touch;
        }
        
        .player-board-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          flex: 0 0 auto;
          min-width: 0;
          overflow: hidden;
          box-sizing: border-box;
        }
        
        .player-board-wrapper {
          width: auto;
          height: auto;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          overflow: visible;
          box-sizing: border-box;
        }
        
        .player-board-item .player-board {
          width: auto;
          height: auto;
          flex-shrink: 0;
        }
        
        .player-board-item canvas {
          display: block;
          /* Canvas size is set directly via width/height attributes, no CSS scaling */
        }
        
        .player-board-item .player-info {
          margin-top: 2px;
          text-align: center;
          font-size: 10px;
          height: 18px;
          min-height: 18px;
          max-height: 18px;
          overflow: hidden;
          flex-shrink: 0;
          box-sizing: border-box;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 100%;
        }
        
        
        .player-board-item .player-info h4 {
          margin: 0;
          padding: 0;
          display: block;
          visibility: visible;
          color: inherit;
          overflow: hidden;
          text-overflow: clip;
          white-space: nowrap;
          max-width: 100%;
          width: 100%;
          line-height: 1.2;
          box-sizing: border-box;
          text-align: center;
          word-break: break-word;
          hyphens: none;
        }
        
        .status-active {
          color: var(--success);
          font-weight: 600;
        }
        
        .status-inactive {
          color: var(--danger);
          font-weight: 600;
        }
        
        .no-players {
          text-align: center;
          color: var(--text-secondary);
          padding: var(--spacing-sm);
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 20px;
          font-size: 12px;
        }
      `}</style>
    </div>
  );
};

export default OtherPlayersView;
